import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Save, SendHorizonal, Loader2, Plus, X } from "lucide-react";

const SINGLE_KEYS = [
  "resend_api_key",
  "email_from_address",
  "email_from_name",
] as const;

const ADMIN_KEY = "email_admin_address";
const MAX_ADMINS = 5;

type SingleKey = (typeof SINGLE_KEYS)[number];

const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

export function EmailSettingsSection() {
  const [values, setValues] = useState<Record<SingleKey, string>>({
    resend_api_key: "",
    email_from_address: "",
    email_from_name: "",
  });
  const [adminEmails, setAdminEmails] = useState<string[]>([""]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("settings")
        .select("key, value")
        .in("key", [...SINGLE_KEYS, ADMIN_KEY]);
      if (data) {
        const next = { ...values };
        let admins: string[] = [""];
        data.forEach((r) => {
          if (SINGLE_KEYS.includes(r.key as SingleKey)) {
            next[r.key as SingleKey] = r.value;
          } else if (r.key === ADMIN_KEY) {
            const list = (r.value || "")
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean);
            admins = list.length > 0 ? list.slice(0, MAX_ADMINS) : [""];
          }
        });
        setValues(next);
        setAdminEmails(admins);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateAdmin = (idx: number, val: string) => {
    setAdminEmails((arr) => arr.map((e, i) => (i === idx ? val : e)));
  };
  const addAdmin = () => {
    if (adminEmails.length < MAX_ADMINS) setAdminEmails((arr) => [...arr, ""]);
  };
  const removeAdmin = (idx: number) => {
    setAdminEmails((arr) => (arr.length === 1 ? [""] : arr.filter((_, i) => i !== idx)));
  };

  const cleanAdmins = () => adminEmails.map((e) => e.trim()).filter(Boolean);

  const upsertSetting = async (key: string, value: string) => {
    const { data: existing } = await supabase
      .from("settings")
      .select("id")
      .eq("key", key)
      .maybeSingle();
    if (existing) {
      const { error } = await supabase.from("settings").update({ value }).eq("id", existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase.from("settings").insert({ key, value });
      if (error) throw error;
    }
  };

  const handleSave = async () => {
    const admins = cleanAdmins();
    const invalid = admins.filter((e) => !isValidEmail(e));
    if (invalid.length > 0) {
      toast({ title: "Correo inválido", description: `Revisa: ${invalid.join(", ")}`, variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      for (const key of SINGLE_KEYS) {
        await upsertSetting(key, values[key]);
      }
      await upsertSetting(ADMIN_KEY, admins.join(","));
      toast({ title: "Guardado", description: "Configuración de correo actualizada." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    const admins = cleanAdmins();
    if (!values.resend_api_key || !values.email_from_address || admins.length === 0) {
      toast({
        title: "Faltan datos",
        description: "Completa al menos la API Key, correo remitente y un correo admin.",
        variant: "destructive",
      });
      return;
    }
    const invalid = admins.filter((e) => !isValidEmail(e));
    if (invalid.length > 0) {
      toast({ title: "Correo inválido", description: `Revisa: ${invalid.join(", ")}`, variant: "destructive" });
      return;
    }
    setTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-test-email", {
        body: {
          to: admins,
          fromEmail: values.email_from_address,
          fromName: values.email_from_name || "Newline Shuttle",
          apiKey: values.resend_api_key,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "Enviado", description: `Correo de prueba enviado a ${admins.length} destinatario(s).` });
    } catch (e: any) {
      toast({ title: "Error al enviar", description: e.message, variant: "destructive" });
    } finally {
      setTesting(false);
    }
  };

  const field = (key: SingleKey, label: string, placeholder: string, type = "text") => (
    <div className="space-y-2">
      <Label htmlFor={key}>{label}</Label>
      <Input
        id={key}
        type={type}
        placeholder={placeholder}
        value={values[key]}
        onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
      />
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Configuración de Correo</h2>
        <p className="text-muted-foreground text-sm mt-1">Configura el servicio de envío de correos con Resend</p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" /> Credenciales Resend
          </CardTitle>
          <CardDescription>Los datos se almacenan de forma segura en la base de datos.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {field("resend_api_key", "API Key de Resend", "re_xxxxxxxxxx", "password")}
          {field("email_from_address", "Correo Remitente", "noreply@tudominio.com")}
          {field("email_from_name", "Nombre Remitente", "Newline Shuttle")}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Correos Administradores (máx. {MAX_ADMINS})</Label>
              <span className="text-xs text-muted-foreground">{cleanAdmins().length}/{MAX_ADMINS}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Todas las notificaciones de reservas llegarán a estos correos.
            </p>
            <div className="space-y-2">
              {adminEmails.map((email, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder={`admin${idx + 1}@tudominio.com`}
                    value={email}
                    onChange={(e) => updateAdmin(idx, e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeAdmin(idx)}
                    disabled={adminEmails.length === 1 && !email}
                    aria-label="Eliminar correo"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addAdmin}
              disabled={adminEmails.length >= MAX_ADMINS}
            >
              <Plus className="h-4 w-4 mr-1" /> Agregar correo
            </Button>
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </Button>
            <Button variant="outline" onClick={handleTest} disabled={testing}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
              Enviar Correo de Prueba
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
